package com.parmartechnologies.commerce.feature.inventory.product.viewModel

import com.parmartechnologies.core.feature.currency.model.Currency
import com.parmartechnologies.core.feature.product.model.Product
import com.parmartechnologies.core.feature.product.model.ProductPrice
import com.parmartechnologies.core.feature.product.network.BacklistProductRequest
import com.parmartechnologies.core.feature.product.network.CreateProductRequest
import com.parmartechnologies.core.feature.product.network.DeleteProductRequest
import com.parmartechnologies.core.feature.product.network.RetrieveProductRequest
import com.parmartechnologies.core.feature.product.network.UpdateProductRequest
import com.parmartechnologies.core.feature.productCategory.model.ProductCategory
import com.parmartechnologies.core.app.AppViewModel
import com.parmartechnologies.commerce.feature.inventory.product.handler.ProductHandler
import com.parmartechnologies.commerce.feature.inventory.product.network.ProductNetwork
import com.parmartechnologies.commerce.feature.inventory.productCategory.handler.ProductCategoryHandler
import com.parmartechnologies.commerce.feature.inventory.productSubCategory.handler.ProductSubCategoryHandler
import com.parmartechnologies.communication.core.handler.AuthHandler
import com.parmartechnologies.core.feature.business.model.Business
import com.parmartechnologies.storage.table.inventory.product.ProductDatabaseTable
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class ProductViewModel(val business: Business) : AppViewModel() {
    val repository = ProductHandler.shared().repository
    val searchQuery: StateFlow<String>
        get() = repository.searchQuery
    val allProduct: StateFlow<List<Product>>
        get() = ProductHandler.shared().repository.allProduct

    val products: StateFlow<List<Product>>
        get() = ProductHandler.shared().repository.products

    val newProduct: StateFlow<Product>
        get() = ProductHandler.shared().repository.newProduct
    val editProduct: StateFlow<Product>
        get() = ProductHandler.shared().repository.editProduct


    val productFormIndex: StateFlow<Int>
        get() = ProductHandler.shared().repository.productFormIndex

    val selectedProductState: MutableStateFlow<Product?> = MutableStateFlow(null)
    val selectedProduct: StateFlow<Product?>
        get() = repository.selectedProduct

    fun setProductFormIndex(value: Int) {
        repository.setProductFormIndex(value)
    }

    override fun changeSearchQuery(value: String) {
        scope.launch {
            repository.searchQueryState.emit(value)
        }
        performSearch(value)
    }

    override fun performSearch(value: String) {
        if (value.isEmpty()) {
            loadProductsFromStorage()
        } else {
            val newList : MutableList<Product> = mutableListOf()
            scope.launch {
                repository.productsState.emit(listOf())
            }
            allProduct.value.forEach{prd->
                if(prd.Name.contains(value,ignoreCase=true) || prd.Description.contains(value,ignoreCase=true)){
                    newList.add(prd)
                }
            }
            scope.launch {
                repository.productsState.emit(newList)
            }
        }
    }

    fun setSelectedProduct(value: Product) {
        scope.launch {
            repository.selectedProductState.emit(value)
        }
    }

    fun setEditProduct(value: Product?) {
        if (value != null) {
            repository.setEditProduct(value)
        }
    }

    fun editProductName(value: String) {
        repository.editProduct.value.Name = value
    }

    fun editProductSalePrice(value: String) {
        repository.editProduct.value.ProductPrice.FinalPrice = value.toDouble()
    }

    fun setNewProduct(value: Product) {
        repository.setNewProduct(value)
    }

    fun setNewProductName(value: String) {
        val existingProduct = repository.newProduct.value
        existingProduct.Name = value
        repository.setNewProduct(existingProduct)
    }

    fun setNewProductDescription(value: String) {
        repository.newProduct.value.Description = value
    }

    fun setNewProductCategory(value: ProductCategory) {
        repository.newProduct.value.CategoryID = value._id
//        val product = repository.newProduct.value
//        product.CategoryID = value._id
//        repository.setNewProduct(product)
    }

    fun setNewProductMrpPrice(value: String) {
        repository.newProduct.value.ProductPrice.MRP = value.toDouble()
    }

    fun setNewProductCostPricePrice(value: String) {
        repository.newProduct.value.ProductPrice.FinalPrice = value.toDouble()
    }

    fun setNewProductFinalPricePrice(value: String) {
        repository.newProduct.value.ProductPrice.FinalPrice = value.toDouble()
    }

    fun setNewProductSGST(value: String) {
        repository.newProduct.value.ProductPrice.SGST = value.toDouble()
    }

    fun setNewProductCGST(value: String) {
        repository.newProduct.value.ProductPrice.CGST = value.toDouble()
    }

    fun setNewProductIGST(value: String) {
        repository.newProduct.value.ProductPrice.IGST = value.toDouble()
    }

    fun setNewProductCESS(value: String) {
        repository.newProduct.value.ProductPrice.CESS = value.toDouble()
    }

    fun setNewProductVAT(value: String) {
        repository.newProduct.value.ProductPrice.VAT = value.toDouble()
    }

    fun setNewProductOtherTax(value: String) {
        repository.newProduct.value.ProductPrice.OtherTax = value.toDouble()
    }

    @OptIn(ExperimentalTime::class)
    fun createProduct(
        callBack: (Boolean) -> Unit
    ) {
        val request = CreateProductRequest(
            Clock.System.now().epochSeconds,
            AuthHandler.shared().UserID,
            business._id,
            categoryID = ProductCategoryHandler.shared().repository.selectedCategory.value!!._id,
            subCategoryID = ProductSubCategoryHandler.shared().repository.selectedSubCategory.value!!._id,
            name = repository.newProduct.value.Name,
            description = repository.newProduct.value.Description,
            productPrice = repository.newProduct.value.ProductPrice
        )
        startLoading()
        scope.launch {
            ProductNetwork().create(request) {
                stopLoading()
                if (!it?.payload?._id.isNullOrEmpty()) {
                    ProductDatabaseTable().insert(it.payload)
                    callBack(true)
                    scope.launch {
                        repository.newProductState.emit(it.payload!!)
                    }
                } else {
                    callBack(false)
                }
            }
        }
    }

    @OptIn(ExperimentalTime::class)
    fun createNewProduct(
        isProduct: Boolean,
        name: String,
        qty: Long,
        description: String,
        categoryId: String,
        subCategoryId: String,
        costPrice: Double,
        mrp: Double,
        discount: Double,
        price: Double,
        salePrice: Double,
        finalPrice: Double,
        cgst: Double,
        sgst: Double,
        igst: Double,
        currency: Currency,
        taxIncluded: Int,
        totalTax: Double,
        callBack: (Boolean) -> Unit
    ) {
        val productPrice = ProductPrice(
            CostPrice = costPrice,
            Price = price,
            SalePrice = salePrice,
            MRP = mrp,
            FinalPrice = finalPrice,
            SGST = sgst,
            CGST = cgst,
            IGST = igst,
            Discount = discount,
            currency = currency,
            taxIncluded=taxIncluded,
            Tax=totalTax
        )
        var productType = "product"
        if (!isProduct) {
            productType = "Service"
        }
        val request = CreateProductRequest(
            Clock.System.now().epochSeconds,
            productType = productType,
            AuthHandler.shared().UserID,
            business._id,
            categoryID = categoryId,
            subCategoryID = subCategoryId,
            name = name,
            qty=qty,
            description = description,
            productPrice = productPrice
        )
        startLoading()
        scope.launch {
            ProductNetwork().create(request) {
                stopLoading()
                if (!it?.payload?._id.isNullOrEmpty()) {
                    ProductDatabaseTable().insert(it?.payload)
                    callBack(true)
                    scope.launch {
                        repository.newProductState.emit(it!!.payload!!)
                    }
                } else {
                    callBack(false)
                }
            }
        }
    }

    @OptIn(ExperimentalTime::class)
    fun updateProduct(
        _id: String,
        isProduct: Boolean,
        name: String,
        qty: Long,
        description: String,
        categoryId: String,
        subCategoryId: String,
        costPrice: Double,
        mrp: Double,
        discount: Double,
        price: Double,
        salePrice: Double,
        finalPrice: Double,
        cgst: Double,
        sgst: Double,
        igst: Double,
        currency: Currency,
        taxIncluded: Int,
        totalTax: Double,
        callBack: (Boolean) -> Unit
    ) {
        val productPrice = ProductPrice(
            CostPrice = costPrice,
            Price = price,
            SalePrice = salePrice,
            MRP = mrp,
            FinalPrice = finalPrice,
            SGST = sgst,
            CGST = cgst,
            IGST = igst,
            Discount = discount,
            currency = currency,
            taxIncluded= taxIncluded,
            Tax = totalTax
        )
        var productType = "product"
        if (!isProduct) {
            productType = "Service"
        }
        var categoryIdToSend: String = ""
        var subCategoryIdToSend: String = ""
        if (categoryId.isEmpty()) {
            categoryIdToSend = ""
        }
        if (subCategoryId.isEmpty()) {
            subCategoryIdToSend = ""
        }
        val request = UpdateProductRequest(
            _id = _id,
            Clock.System.now().epochSeconds,
            productType = productType,
            AuthHandler.shared().UserID,
            business._id,
            categoryID = categoryIdToSend,
            subCategoryID = subCategoryIdToSend,
            name = name,
            qty=qty,
            description = description,
            productPrice = productPrice
        )
        startLoading()
        scope.launch {
            ProductNetwork().update(request) {
                stopLoading()
                if (!it?.payload?._id.isNullOrEmpty()) {
                    ProductDatabaseTable().insert(it.payload)
                    callBack(true)
                    scope.launch {
                        repository.newProductState.emit(it.payload!!)
                    }
                } else {
                    callBack(false)
                }
            }
        }
    }

    fun loadProduct() {
        loadProductsFromStorage()
        ProductDatabaseTable().LastSyncDate(business._id) { date ->
            val request = RetrieveProductRequest(
                business._id,
                date ?: ""
            )
            if (products.value.isEmpty()) {
                startLoading()
            }
            ProductNetwork().retrieve(request) {
                stopLoading()
                ProductDatabaseTable().insert(it?.payload) {
                    loadProductsFromStorage()
                }
            }
        }
    }

    fun loadProductForBusiness(id: String) {
        loadProductsForBusinessFromStorage(id)
        ProductDatabaseTable().LastSyncDate(id) { date ->
            val request = RetrieveProductRequest(
                id,
                date ?: ""
            )
            ProductNetwork().retrieve(request) { productResponse->
                ProductDatabaseTable().insert(productResponse?.payload) {

                }
                scope.launch {
                    repository.allProductState.emit(productResponse?.payload ?: listOf())
                    repository.productsState.emit(productResponse?.payload ?: listOf())
                }
            }
        }
    }

    fun searchProductsForBusiness(id: String, query: String) {
        ProductDatabaseTable().search(id, query) { data ->
            scope.launch {
                repository.allProductState.emit(data)
                repository.productsState.emit(data)
            }
        }
    }

    private fun loadProductsFromStorage() {
        ProductDatabaseTable().retrieve(business._id) { data ->
            scope.launch {
                repository.allProductState.emit(data)
                repository.productsState.emit(data)
            }
        }
    }

    private fun loadProductsForBusinessFromStorage(id: String) {
        ProductDatabaseTable().retrieve(id) { data ->
            scope.launch {
                repository.allProductState.emit(data)
                repository.productsState.emit(data)
            }
        }
    }

    private fun loadProductsFromStorage(id: String) {
        ProductDatabaseTable().retrieve(id) { data ->
            scope.launch {
                repository.allProductState.emit(data)
                repository.productsState.emit(data)
            }
        }
    }

    fun loadProductForCategory(id: String, categoryId: String?) {
        if (categoryId != null) {
            ProductDatabaseTable().retrieve(id, categoryId) { data ->
                scope.launch {
                    repository.allProductState.emit(data)
                    repository.productsState.emit(data)
                }
            }
        } else {
            scope.launch {
                repository.allProductState.emit(arrayListOf())
                repository.productsState.emit(arrayListOf())
            }
        }
    }


    fun loadProductForSubCategory(id: String, subcategoryId: String?) {
        if (subcategoryId != null) {
            ProductDatabaseTable().retrieveSubCategory(id, subcategoryId) { data ->
                scope.launch {
                    repository.allProductState.emit(data)
                    repository.productsState.emit(data)
                }
            }
        } else {
            scope.launch {
                repository.allProductState.emit(arrayListOf())
                repository.productsState.emit(arrayListOf())
            }
        }
    }


    fun deleteProduct(product: Product, callBack: (Boolean) -> Unit) {
        val request = DeleteProductRequest(
            _id = product._id,
        )
        startLoading()
        scope.launch {
            ProductNetwork().delete(request) {
                stopLoading()
                if (it?.status?.equals("SUCCESS") == true) {
                    callBack(true)
                } else {
                    callBack(false)
                }
            }
        }
    }

    fun blacklistProduct(product: Product, callBack: (Boolean) -> Unit) {
        val request = BacklistProductRequest(
            _id = product._id,
            isBlacklisted = product.IsBlacklisted,
        )
        startLoading()
        scope.launch {
            ProductNetwork().blacklist(request) {
                stopLoading()
                if (it?.status?.equals("SUCCESS") == true) {
                    callBack(true)
                } else {
                    callBack(false)
                }
            }
        }
    }
}
